import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { User, Edit, ChevronDown, ChevronRight } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/hiring`;

const OrgNode = ({ employee, level = 0, onEmployeeClick, expandedNodes, toggleNode }) => {
  const hasChildren = employee.children && employee.children.length > 0;
  const isTopLevel = level === 0;
  const isFirstLevel = level === 1;
  const isExpanded = expandedNodes[employee.id];

  return (
    <div className="flex flex-col items-center">
      {/* Employee Box */}
      <div
        className={`relative cursor-pointer transition-all hover:scale-105 ${
          isTopLevel ? 'w-64' : 'w-56'
        }`}
      >
        <div
          onClick={() => onEmployeeClick(employee)}
          className={`rounded-lg shadow-lg border-2 p-4 ${
            isTopLevel
              ? 'bg-gradient-to-br from-blue-500 to-blue-600 border-blue-700 text-white'
              : 'bg-white border-gray-300 hover:border-blue-400'
          }`}
        >
          <div className="flex items-center gap-3">
            <div
              className={`p-2 rounded-full ${
                isTopLevel ? 'bg-white/20' : 'bg-blue-100'
              }`}
            >
              <User className={`h-5 w-5 ${isTopLevel ? 'text-white' : 'text-[#C9A64A]'}`} />
            </div>
            <div className="flex-1 min-w-0">
              <div className={`font-bold truncate ${isTopLevel ? 'text-lg' : 'text-base'}`}>
                {employee.name}
              </div>
              <div className={`text-sm truncate ${isTopLevel ? 'text-blue-100' : 'text-gray-600'}`}>
                {employee.job_title}
              </div>
            </div>
          </div>
          <div className="mt-2 flex gap-2">
            <Badge
              variant="outline"
              className={`text-xs ${isTopLevel ? 'border-white/40 text-white' : ''}`}
            >
              {employee.team}
            </Badge>
            <Badge
              variant="secondary"
              className={`text-xs ${isTopLevel ? 'bg-white/20 text-white border-white/40' : ''}`}
            >
              {employee.employee_id}
            </Badge>
          </div>
          {hasChildren && (
            <div className={`mt-2 text-xs ${isTopLevel ? 'text-blue-100' : 'text-gray-500'}`}>
              {employee.children.length} direct report{employee.children.length !== 1 ? 's' : ''}
            </div>
          )}
        </div>
        
        {/* Expand/Collapse Button */}
        {hasChildren && !isTopLevel && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              toggleNode(employee.id);
            }}
            className="absolute -bottom-4 left-1/2 -translate-x-1/2 w-8 h-8 rounded-full bg-white border-2 border-gray-300 hover:border-blue-500 hover:bg-blue-50 flex items-center justify-center shadow-md transition-all z-10"
          >
            {isExpanded ? (
              <ChevronDown className="h-4 w-4 text-gray-600" />
            ) : (
              <ChevronRight className="h-4 w-4 text-gray-600" />
            )}
          </button>
        )}
      </div>

      {/* Children - Only show if expanded or at first level */}
      {hasChildren && (isTopLevel || isExpanded) && (
        <div className="relative mt-8">
          {/* Vertical line from parent */}
          <div className="absolute top-0 left-1/2 w-0.5 h-4 bg-gray-300 -translate-x-1/2 -translate-y-8" />
          
          <div className="flex gap-8 justify-center">
            {employee.children.map((child, idx) => (
              <div key={child.id} className="relative">
                {/* Vertical line to child */}
                <div className="absolute top-0 left-1/2 w-0.5 h-4 bg-gray-300 -translate-x-1/2 -translate-y-4" />
                
                {/* Horizontal line */}
                {employee.children.length > 1 && (
                  <div
                    className="absolute top-0 h-0.5 bg-gray-300 -translate-y-4"
                    style={{
                      left: idx === 0 ? '50%' : '-4rem',
                      right: idx === employee.children.length - 1 ? '50%' : '-4rem',
                    }}
                  />
                )}
                
                <OrgNode 
                  employee={child} 
                  level={level + 1} 
                  onEmployeeClick={onEmployeeClick}
                  expandedNodes={expandedNodes}
                  toggleNode={toggleNode}
                />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const EmployeeDetailsDialog = ({ employee, isOpen, onClose, onEdit }) => {
  if (!employee) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <div className="flex justify-between items-start">
            <DialogTitle className="text-xl">Employee Details</DialogTitle>
            <Button
              variant="ghost"
              size="icon"
              onClick={onEdit}
              className="-mt-2 -mr-2"
            >
              <Edit className="h-5 w-5" />
            </Button>
          </div>
        </DialogHeader>
        <div className="space-y-4">
          <div className="flex items-center gap-4">
            <div className="p-4 rounded-full bg-blue-100">
              <User className="h-8 w-8 text-[#C9A64A]" />
            </div>
            <div>
              <div className="font-bold text-lg">{employee.name}</div>
              <div className="text-gray-600">{employee.job_title}</div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 pt-4 border-t">
            <div>
              <div className="text-sm text-gray-600">Employee ID</div>
              <div className="font-semibold">{employee.employee_id}</div>
            </div>
            <div>
              <div className="text-sm text-gray-600">Team</div>
              <div className="font-semibold">{employee.team}</div>
            </div>
            <div>
              <div className="text-sm text-gray-600">Salary (USD)</div>
              <div className="font-semibold text-green-600">
                ${employee.salary_usd?.toLocaleString()}
              </div>
            </div>
            <div>
              <div className="text-sm text-gray-600">Start Date</div>
              <div className="font-semibold">
                {new Date(employee.start_date).toLocaleDateString()}
              </div>
            </div>
            <div>
              <div className="text-sm text-gray-600">Cost Center</div>
              <div className="font-semibold">{employee.cost_center || '-'}</div>
            </div>
            <div>
              <div className="text-sm text-gray-600">Manager</div>
              <div className="font-semibold">{employee.manager_name || 'None'}</div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const ImprovedOrgChart = () => {
  const [orgData, setOrgData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [fullEmployeeData, setFullEmployeeData] = useState(null);
  const [formData, setFormData] = useState({});
  const [expandedNodes, setExpandedNodes] = useState({});
  const { toast } = useToast();

  useEffect(() => {
    fetchOrgChart();
  }, []);

  const fetchOrgChart = async () => {
    try {
      const response = await axios.get(`${API}/organizational-chart`);
      setOrgData(response.data.org_chart);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching org chart:', error);
      setLoading(false);
    }
  };

  const toggleNode = (nodeId) => {
    setExpandedNodes(prev => ({
      ...prev,
      [nodeId]: !prev[nodeId]
    }));
  };

  const expandAll = () => {
    const allNodeIds = {};
    const collectNodeIds = (nodes) => {
      nodes.forEach(node => {
        allNodeIds[node.id] = true;
        if (node.children && node.children.length > 0) {
          collectNodeIds(node.children);
        }
      });
    };
    collectNodeIds(orgData);
    setExpandedNodes(allNodeIds);
  };

  const collapseAll = () => {
    setExpandedNodes({});
  };

  const handleEmployeeClick = async (employee) => {
    try {
      // Fetch full employee details
      const response = await axios.get(`${API}/employees/${employee.id}`);
      setFullEmployeeData(response.data);
      setSelectedEmployee(employee);
      setIsDetailsOpen(true);
    } catch (error) {
      console.error('Error fetching employee details:', error);
    }
  };

  const handleEdit = () => {
    setIsDetailsOpen(false);
    setFormData(fullEmployeeData);
    setIsEditOpen(true);
  };

  const handleSaveEdit = async () => {
    try {
      await axios.put(`${API}/employees/${fullEmployeeData.id}`, formData);
      toast({ title: 'Success', description: 'Employee updated successfully' });
      setIsEditOpen(false);
      fetchOrgChart();
    } catch (error) {
      console.error('Error updating employee:', error);
      toast({ title: 'Error', description: 'Failed to update employee', variant: 'destructive' });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg">Loading organizational chart...</div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 bg-gradient-to-br from-slate-50 to-blue-50 min-h-screen">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Organizational Chart</h1>
          <p className="text-gray-600 mt-1">
            Click on any employee to view details and edit information
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={expandAll} variant="outline" size="sm">
            Expand All
          </Button>
          <Button onClick={collapseAll} variant="outline" size="sm">
            Collapse All
          </Button>
        </div>
      </div>

      <Card className="shadow-md overflow-x-auto">
        <CardHeader>
          <CardTitle>Company Hierarchy</CardTitle>
        </CardHeader>
        <CardContent className="p-8 min-w-max">
          {orgData.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No organizational data available. Add manager relationships to employees.
            </div>
          ) : (
            <div className="flex justify-center min-w-full px-8">
              {orgData.map((employee) => (
                <OrgNode
                  key={employee.id}
                  employee={employee}
                  level={0}
                  onEmployeeClick={handleEmployeeClick}
                  expandedNodes={expandedNodes}
                  toggleNode={toggleNode}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <EmployeeDetailsDialog
        employee={fullEmployeeData}
        isOpen={isDetailsOpen}
        onClose={() => setIsDetailsOpen(false)}
        onEdit={handleEdit}
      />

      {/* Edit Employee Dialog */}
      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Employee</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Name</Label>
              <Input
                value={formData.name || ''}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Job Title</Label>
              <Input
                value={formData.job_title || ''}
                onChange={(e) => setFormData({ ...formData, job_title: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Manager</Label>
              <Input
                value={formData.manager_name || ''}
                onChange={(e) => setFormData({ ...formData, manager_name: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Cost Center</Label>
              <Input
                value={formData.cost_center || ''}
                onChange={(e) => setFormData({ ...formData, cost_center: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Salary (USD)</Label>
              <Input
                type="number"
                value={formData.salary_usd || 0}
                onChange={(e) => setFormData({ ...formData, salary_usd: parseFloat(e.target.value) })}
              />
            </div>
            <div className="space-y-2">
              <Label>Team</Label>
              <Input
                value={formData.team || ''}
                onChange={(e) => setFormData({ ...formData, team: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveEdit}>Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ImprovedOrgChart;
